/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { AxiosError } from 'axios';

import { colors, mixins } from '@blend/consumer-components';
import { useDispatch } from 'react-redux';
import { zIndex } from '../../../style/zIndex';
import { notaryAPI } from '../../../api/notary';
import { addApiErrorNotification } from '../../../actions/pageActions';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { diagnostics } from '@utils/diagnostics';

export const FINAL_DOCUMENT_PACKAGE_MESSAGE = 'Review this document package to finalize the notarization.';
export const NON_FINAL_DOCUMENT_PACKAGE_MESSAGE = 'Review this document package before moving on to the next one.';

export const FINAL_DOCUMENT_PACKAGE_BUTTON_TEXT = 'Finish';
export const NON_FINAL_DOCUMENT_PACKAGE_BUTTON_TEXT = 'Continue';

export namespace DocumentPackageReviewBar {
  export interface Props {
    callId: string;
    documentIds: string[];
    isLastUnsignedDocumentPackage: boolean;
  }
}

const BAR_WIDTH = 572;

const styles = StyleSheet.create({
  wrapper: {
    position: 'fixed',
    width: BAR_WIDTH,
    minHeight: 60,
    backgroundColor: colors.catalystLight01,
    zIndex: zIndex.reviewBar,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    boxShadow: mixins.boxShadow(0, 1, 4, 0, mixins.rgba(0, 0, 0, 0.25)),
    borderRadius: 5,
    bottom: 24,
    // center in the middle of the main content (excluding the sidebar)
    left: `calc(50% + 161px)`,
    marginLeft: -1 * (BAR_WIDTH / 2),
  },
});

export const DocumentPackageReviewBar: React.FC<DocumentPackageReviewBar.Props> = ({
  callId,
  documentIds,
  isLastUnsignedDocumentPackage,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = React.useState(false);

  const reviewDocuments = async () => {
    try {
      setSubmitting(true);
      await Promise.all(
        documentIds.map(async id => {
          await notaryAPI.documents.reviewDocument(callId, id);
        }),
      );
    } catch (err) {
      addApiErrorNotification('Error reviewing document package', err as AxiosError<{ message?: string }>)(dispatch);
      diagnostics.error(err as Error, {
        additionalMeta: 'Error reviewing document package',
        callId,
        documentIds,
      });
    }

    setSubmitting(false);
  };

  const message = isLastUnsignedDocumentPackage ? FINAL_DOCUMENT_PACKAGE_MESSAGE : NON_FINAL_DOCUMENT_PACKAGE_MESSAGE;
  const buttonText = isLastUnsignedDocumentPackage
    ? FINAL_DOCUMENT_PACKAGE_BUTTON_TEXT
    : NON_FINAL_DOCUMENT_PACKAGE_BUTTON_TEXT;

  return (
    <div className={css(styles.wrapper)} data-it="DocumentPackageReviewBar">
      <span data-it="DocumentPackageReviewBar-message">{message}</span>
      <ActionButton
        data-dd-privacy="allow"
        data-it="DocumentPackageReviewBar-submit-button"
        onClick={reviewDocuments}
        sizing="sm"
        disabled={submitting}
      >
        {buttonText}
      </ActionButton>
    </div>
  );
};
